/**
 * Stage Plot — natural-language placement assistant (LOT C).
 *
 * Pure, DOM-free helpers shared by the StagePlotAssistant panel and the editor
 * store. The AI (POST /api/ai/stageplot-command) only PROPOSES structured
 * actions; this module validates them against a strict allowlist and applies
 * them to a COPY of the elements (immutable), so a bad action can never break
 * the plot and the input array is never mutated.
 *
 * The server already validates every action; this client-side pass is the
 * second line of defense (and the source of the human-readable descriptions).
 */

import {
  PALETTE_ITEMS,
  CATEGORY_COLORS,
  type StageElement,
  type StageDimensions,
  type PaletteCategory,
} from '../shared/types/stageplot';

/** The closed set of ops the assistant may propose. */
export const STAGEPLOT_OPS = ['add', 'move', 'remove', 'rotate', 'arrange'] as const;
export type StageCommandOp = (typeof STAGEPLOT_OPS)[number];

export const STAGEPLOT_ARRANGE_PRESETS = ['standard', 'backline', 'monitors'] as const;
export type StageArrangePreset = (typeof STAGEPLOT_ARRANGE_PRESETS)[number];

export type StageCommandAction =
  | { op: 'add'; iconId: string; x: number; y: number; label?: string }
  | { op: 'move'; id: string; x: number; y: number }
  | { op: 'remove'; id: string }
  | { op: 'rotate'; id: string; rotation: number }
  | { op: 'arrange'; preset: StageArrangePreset };

export interface StageCommandResult {
  actions: StageCommandAction[];
  rejected: unknown[];
}

export interface ApplyResult {
  elements: StageElement[];
  applied: StageCommandAction[];
  rejected: unknown[];
}

function newElementId(): string {
  return `ai-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 7)}`;
}

function clamp(v: number, max: number): number {
  return Math.max(0, Math.min(max, v));
}

function isFiniteNum(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/** True when `iconId` exists in the closed palette. */
export function isValidStageIconId(iconId: unknown): boolean {
  return typeof iconId === 'string' && PALETTE_ITEMS.some((i) => i.iconId === iconId);
}

/** Build a StageElement for an `add` action (palette defaults + category colour). */
export function buildElementForAdd(
  action: { iconId: string; x: number; y: number; label?: string },
  stage: StageDimensions,
): StageElement | null {
  const item = PALETTE_ITEMS.find((i) => i.iconId === action.iconId);
  if (!item) return null;
  const category = item.category as PaletteCategory;
  return {
    id: newElementId(),
    iconId: item.iconId,
    category,
    label: action.label && action.label.trim() ? action.label.trim() : item.label,
    x: clamp(action.x, stage.width),
    y: clamp(action.y, stage.height),
    width: item.defaultWidth,
    height: item.defaultHeight,
    rotation: 0,
    color: CATEGORY_COLORS[category],
  };
}

/** Deterministic repositioning for an `arrange` preset. Returns a new array. */
export function arrangeElements(
  elements: StageElement[],
  preset: StageArrangePreset,
  stage: StageDimensions,
): StageElement[] {
  if (elements.length === 0) return elements;
  const n = elements.length;
  const margin = 0.5;
  const usableW = Math.max(0.5, stage.width - margin * 2);
  const usableH = Math.max(0.5, stage.height - margin * 2);

  if (preset === 'backline') {
    // Single row along the upstage edge (y = margin), evenly spaced.
    const step = n === 1 ? 0 : usableW / (n - 1);
    return elements.map((el, i) => ({ ...el, x: Math.round((margin + step * i) * 100) / 100, y: margin }));
  }
  if (preset === 'monitors') {
    // Single row along the downstage edge (y = height - margin), evenly spaced.
    const step = n === 1 ? 0 : usableW / (n - 1);
    return elements.map((el, i) => ({
      ...el,
      x: Math.round((margin + step * i) * 100) / 100,
      y: Math.round((stage.height - margin) * 100) / 100,
    }));
  }
  // 'standard': a grid from upstage to downstage, left to right.
  const cols = Math.max(1, Math.ceil(Math.sqrt(n)));
  const rows = Math.ceil(n / cols);
  const cellW = usableW / cols;
  const cellH = usableH / rows;
  return elements.map((el, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    return {
      ...el,
      x: Math.round((margin + cellW * (col + 0.5)) * 100) / 100,
      y: Math.round((margin + cellH * (row + 0.5)) * 100) / 100,
    };
  });
}

/**
 * Tolerantly parse the assistant's response into validated actions. Accepts the
 * API response `{ reply, actions, rejected }` OR a bare actions array. Every
 * action is checked against the allowlist; invalid ones are moved to
 * `rejected`. Never throws.
 */
export function parseStageCommandResult(raw: unknown): StageCommandResult {
  let list: unknown[] = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const a = (raw as { actions?: unknown }).actions;
    if (Array.isArray(a)) list = a;
  }
  const actions: StageCommandAction[] = [];
  const rejected: unknown[] = [];
  for (const a of list) {
    if (!a || typeof a !== 'object' || Array.isArray(a)) { rejected.push(a); continue; }
    const obj = a as Record<string, unknown>;
    const op = obj.op;
    if (!STAGEPLOT_OPS.includes(op as StageCommandOp)) { rejected.push(a); continue; }
    if (op === 'add') {
      if (!isValidStageIconId(obj.iconId) || !isFiniteNum(obj.x) || !isFiniteNum(obj.y)) {
        rejected.push(a);
        continue;
      }
      actions.push({
        op: 'add',
        iconId: obj.iconId as string,
        x: obj.x,
        y: obj.y,
        ...(typeof obj.label === 'string' && obj.label.trim() ? { label: obj.label.trim() } : {}),
      });
    } else if (op === 'move') {
      if (typeof obj.id !== 'string' || !obj.id || !isFiniteNum(obj.x) || !isFiniteNum(obj.y)) {
        rejected.push(a);
        continue;
      }
      actions.push({ op: 'move', id: obj.id, x: obj.x, y: obj.y });
    } else if (op === 'remove') {
      if (typeof obj.id !== 'string' || !obj.id) { rejected.push(a); continue; }
      actions.push({ op: 'remove', id: obj.id });
    } else if (op === 'rotate') {
      if (typeof obj.id !== 'string' || !obj.id || !isFiniteNum(obj.rotation)) {
        rejected.push(a);
        continue;
      }
      actions.push({ op: 'rotate', id: obj.id, rotation: obj.rotation });
    } else if (op === 'arrange') {
      if (!STAGEPLOT_ARRANGE_PRESETS.includes(obj.preset as StageArrangePreset)) {
        rejected.push(a);
        continue;
      }
      actions.push({ op: 'arrange', preset: obj.preset as StageArrangePreset });
    }
  }
  return { actions, rejected };
}

/**
 * Apply validated actions to a COPY of the elements (immutable — the input is
 * never mutated). Positions are clamped to the stage bounds. An invalid action
 * is refused without breaking the rest of the batch: valid actions apply, the
 * invalid ones are listed in `rejected`.
 */
export function applyActionsToElements(
  elements: StageElement[],
  actions: StageCommandAction[],
  stage: StageDimensions,
): ApplyResult {
  let out = elements.map((el) => ({ ...el }));
  const applied: StageCommandAction[] = [];
  const rejected: unknown[] = [];

  for (const action of actions) {
    if (!action || typeof action !== 'object') { rejected.push(action); continue; }
    const op = action.op;
    if (!STAGEPLOT_OPS.includes(op)) { rejected.push(action); continue; }

    if (op === 'add') {
      const el = buildElementForAdd(action, stage);
      if (!el) { rejected.push(action); continue; }
      out = [...out, el];
      applied.push(action);
    } else if (op === 'move') {
      if (!out.some((el) => el.id === action.id)) { rejected.push(action); continue; }
      out = out.map((el) =>
        el.id === action.id
          ? { ...el, x: clamp(action.x, stage.width), y: clamp(action.y, stage.height) }
          : el,
      );
      applied.push(action);
    } else if (op === 'rotate') {
      if (!out.some((el) => el.id === action.id)) { rejected.push(action); continue; }
      out = out.map((el) => (el.id === action.id ? { ...el, rotation: action.rotation } : el));
      applied.push(action);
    } else if (op === 'remove') {
      if (!out.some((el) => el.id === action.id)) { rejected.push(action); continue; }
      out = out.filter((el) => el.id !== action.id);
      applied.push(action);
    } else if (op === 'arrange') {
      out = arrangeElements(out, action.preset, stage);
      applied.push(action);
    } else {
      rejected.push(action);
    }
  }

  return { elements: out, applied, rejected };
}

/** Human-readable description of a single action, e.g. "add Vocal Mic at (2.5, 6.0)". */
export function describeAction(action: StageCommandAction): string {
  switch (action.op) {
    case 'add': {
      const item = PALETTE_ITEMS.find((i) => i.iconId === action.iconId);
      const name = action.label && action.label.trim() ? action.label.trim() : item?.label ?? action.iconId;
      return `add ${name} at (${action.x.toFixed(1)}, ${action.y.toFixed(1)})`;
    }
    case 'move':
      return `move ${action.id} to (${action.x.toFixed(1)}, ${action.y.toFixed(1)})`;
    case 'remove':
      return `remove ${action.id}`;
    case 'rotate':
      return `rotate ${action.id} to ${action.rotation}°`;
    case 'arrange':
      return `arrange (${action.preset})`;
  }
}
